import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Template} from "../../../../../components/template/template.jsx";
import Button from "../../../../../components/button/button.jsx";
import {FaEraser, FaPlus, FaSearch} from "react-icons/fa";
import {Card} from "../../../../../components/card/card.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../../utils/aggrid-utils.js";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import {useGlobalKey} from "../../../../../hooks/use-global-key.js";
import {FaCircleExclamation} from "react-icons/fa6";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import useFetchData from "../../hooks/use-fetch-options.js";
import {useQuery} from "@tanstack/react-query";
import materialAlertService from "../../../../../services/web/material-alert-service.js";
import SentryRow from "../../../../../components/template/sentry-row.jsx";

const initialFilter = {
    material: "",
    storage: ""
}

const MaterialAlertsList = () => {
    const navigate = navigateHook();
    const [filter, setFilter] = useState(initialFilter);
    const [gridApi, setGridApi] = useState(null);
    const gridRef = useRef(null);

    const {storageOptions} = useFetchData({fetchStorage: true});

    const {data: alerts = [], isLoading} = useQuery({
        queryKey: ['materials-kits'],
        queryFn: async () => await materialAlertService.list().then(data => {
            return Object.values(
                data.reduce((acc, item) => {
                    const id = item.id;

                    if (!acc[id]) {
                        acc[id] = {...item, total: item.total ?? 0, caution: item.caution ?? 0, available: item.available ?? 0}
                    } else {
                        acc[id].total += item.total ?? 0;
                        acc[id].caution += item.caution ?? 0;
                        acc[id].available += item.available ?? 0;
                    }

                    return acc;
                }, {})
            )
        }),
    });

    const [columnDefs] = useState([
        {field: "id", hide: true},
        {field: "storage", headerName: "Depósito", flex: 1, filter: "agTextColumnFilter", comparator: aggridUtils.nullLastComparator},
        {field: "material", headerName: "Material", flex: 1, filter: "agTextColumnFilter", filterParams: aggridUtils.filterParams, comparator: aggridUtils.nullLastComparator},
        {field: "total", headerName: "Total", width: 120, comparator: aggridUtils.nullLastComparator},
        {field: "caution", headerName: "Em cautela", width: 120, comparator: aggridUtils.nullLastComparator},
        {field: "available", headerName: "Disponível", width: 120, comparator: aggridUtils.nullLastComparator},
        {field: "quantity", headerName: "Qtd. mínima", width: 130, comparator: aggridUtils.nullLastComparator},
        {field: "alert", headerName: "Alerta", width: 200, comparator: aggridUtils.nullLastComparator,
            cellRenderer: ({value}) => {
                if (value) {
                    return <span className="text-orange-300 font-bold flex items-center">
                                Estoque mínimo
                                <FaCircleExclamation className={"ml-1"} />
                            </span>
                }

                return <></>
            }
        },
    ]);

    const defaultColDef = useMemo(() => {
        return {
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            suppressHeaderContextMenu: true,
            suppressFloatingFilterButton: true,
        };
    }, []);

    const onChange = useCallback(({ target: { id, value } }) => {
        setFilter(prev => ({ ...prev, [id]: value }));
    }, []);

    const search = useCallback(() => {
        if (!gridApi) return;

        const filterModel = {};

        Object.entries(filter).forEach(([field, value]) => {
            if (!value) return;
            let filterValue = value

            if (field === "storage") {
                const storageOption = storageOptions.find(s => s.value === value)
                filterValue = storageOption?.label
            }

            filterModel[field] = {
                filterType: "text",
                type: "contains",
                filter: filterValue
            }
        });

        gridApi.setFilterModel(filterModel);
        gridApi.onFilterChanged();
    }, [filter, gridApi]);

    const clearFilter = useCallback(() => {
        setFilter(initialFilter);
        const api = gridRef.current?.api;
        if (api) {
            api.setFilterModel(null);
            api.onFilterChanged();
        }
    }, []);

    useGlobalKey("Enter", search);
    useGlobalKey("Escape", clearFilter);

    const onRowClicked = ({event, data}) => {
        const id = data.id;
        const urlDev = `/materials/alerts/form/${id}`;
        let isBlank = false
        if (event.ctrlKey) isBlank = true
        navigate(urlDev, isBlank)
    }

    const openForm = () => {
        const urlDev = `/materials/alerts/form`;
        navigate(urlDev)
    }

    return (
        <Template>
            <Template.Header>
                <Template.Title>
                    Alertas
                </Template.Title>
                <Button onClick={openForm}>
                    <FaPlus/><span className="font-bold">Adicionar alerta</span>
                </Button>
            </Template.Header>
            <Template.Body>
                <Card>
                    <Card.Body>
                        <SentryRow>
                            <div className="col-span-5">
                                <SelectComponent
                                    id="storage"
                                    value={filter.storage}
                                    options={storageOptions || []}
                                    label={"Depósito"}
                                    onChange={(selected) => setFilter(prevState => ({...prevState, storage: selected?.value}))}
                                />
                            </div>
                            <div className="col-span-7 flex gap-4">
                                <InputForm id="material" name="material" value={filter.material} onChange={onChange}
                                           label="Descrição" classNameWrapper={"w-full"}/>
                                <div className="flex justify-start gap-2">
                                    <Button target="primary-outline" onClick={clearFilter} title={"Limpar"}>
                                        <FaEraser/>
                                    </Button>
                                    <Button onClick={search} title={"Buscar"}><FaSearch/></Button>
                                </div>
                            </div>
                        </SentryRow>
                    </Card.Body>
                </Card>
                <Card className="mt-2">
                    <Card.Body>
                        <div className={"h-[calc(100vh-153px)] aggrid-row-pointer"}>
                            <AgGridReact
                                onGridReady={(params) => {
                                    setGridApi(params.api);
                                }}
                                ref={gridRef}
                                rowData={alerts || []}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                pagination
                                onRowClicked={onRowClicked}
                                paginationPageSize={50}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus={true}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={aggridUtils.generalTheme()}
                                onFilterChanged={aggridUtils.filterChangedNoRowsOverlay}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    )
};

export default MaterialAlertsList;